import * as React from 'react';
import { connect } from 'react-redux';

import { ExcelUploadAccountingPackageNameId } from '../../Models/Api/AccountingPackage';
import { PortalTextAccountingSystems } from '../../Models/Api/strongbox.financialportal';
import {
    PortalCreateSubmissionRequestOptions,
} from '../../Models/PortalCreateSubmissionRequestOptions';
import { ApplicationState } from '../../Store';
import {
    ConnectionOptions,
    ConnectorType,
    CustomerFinancialsTarget,
    DirectLinkParameters,
    GetConnectionOptions,
    GetImportCustomerFinancialsActive,
    GetImportCustomerFinancialsTarget,
    GetImportingDirect,
    LinkMode,
} from '../../Store/ImportFinancials';
import { FinancialsImportFlowView, GetFinancialsImportFlowView, actionCreators as NewDataCollectionParametersActions } from '../../Store/NewDataCollectionParameters';
import { GetPortalText } from '../../Store/PortalAssets';
import { GetBrandConfig } from '../../Store/Tenant';
import { ConnectWindow } from '../../Utils/ConnectWindow';
import { ImportCustomerFinancials } from '../ImportCustomerFinancials';
import { DataCollectionImportFlow } from './DataCollectionImportFlow';

type InjectedReduxState = {
    customerFinancialsTarget: CustomerFinancialsTarget | undefined;
    importingDirect: boolean;
    directLinkParameters?: DirectLinkParameters;
    connectionOptions?: ConnectionOptions;
    flowView: FinancialsImportFlowView;
    isImportCustomerFinancialsActive: boolean;
    brandAccentColor?: string;
    accountingSystemText?: PortalTextAccountingSystems;
};

type InjectedActionCreators = typeof NewDataCollectionParametersActions;

type FinancialsImportProps = {};

type Props = FinancialsImportProps & InjectedActionCreators & InjectedReduxState;

const FinancialsImportV2Component: React.FC<Props> = (props): React.ReactElement => {
    const {
        connectionOptions,
        importingDirect,
        directLinkParameters,
        ClearCurrentSubmissionRequest,
        SetCurrentSubmissionRequest,
        SetSelectedAccountingPackage,
        CreateUniversalConnectorSubmissionAndStartImport,
        CreateSubmissionAndReuseExistingConnection,
        ReportPopupBlocked,
        flowView,
        isImportCustomerFinancialsActive,
        brandAccentColor,
        accountingSystemText,
    } = props;

    const handleImportSubmit = (linkMode: LinkMode, connectorType: ConnectorType, uiOptions: PortalCreateSubmissionRequestOptions, allowPkgSelection: boolean): void => {
        const request = { connectorType, linkMode, options: uiOptions, ...props.customerFinancialsTarget };

        // "Sync financial data from <accounting system>" (workspace → Manage Active Connections) opens the
        // wizard with the row's accounting package + connection id already known. After the collection-options
        // step, skip the package picker / connect popup entirely and import straight against that connection.
        if (directLinkParameters?.connectionId) {
            CreateSubmissionAndReuseExistingConnection(
                request,
                directLinkParameters.accountingPkg as string,
                directLinkParameters.connectionId
            );
            return;
        }

        if (connectorType === ConnectorType.universalConnector && linkMode === LinkMode.direct) {
            // "Upload files yourself" (LinkMode.direct): the universal collector connect opens a popup, so it
            // MUST be opened synchronously here inside the wizard's Next click for the browser to grant
            // transient activation to window.open. The thunk navigates this already-open window to the real
            // connect URL after the async request/submission setup, and drives the flow straight through the
            // connect with no package picker or confirmation modal (see
            // CreateUniversalConnectorSubmissionAndStartImport).
            //
            // "Create upload link" (LinkMode.shareable) does NOT take this path: it falls through to
            // SetCurrentSubmissionRequest below, which creates a submission request with a shareable link and
            // drives the flow to the ShareLinkModal (secure URL) instead of opening a connect popup.
            const connectWindow = new ConnectWindow();

            const heading = accountingSystemText?.loadingUniversalUpload || "Loading Universal Upload";

            const opened = connectWindow.open({
                heading: heading || 'Connecting to your accounting system…',
                subText: 'Please keep this window open while we finish connecting.',
                accentColor: brandAccentColor,
            });

            if (!opened) {
                ReportPopupBlocked(
                    'Popup blocked',
                    'Your browser blocked the connection window. Please allow popups for this site and try again.'
                );
                return;
            }

            CreateUniversalConnectorSubmissionAndStartImport(request, connectWindow);
            return;
        }

        if (connectorType === ConnectorType.excelUpload) {
            // Excel / external-file upload has a single fixed package, so there is no package picker. Create the
            // submission request and immediately select the excel package so the flow advances straight to the
            // connect confirmation ("Connect to <package>"), skipping the selectAccountingSystem step. Mirrors
            // V1's lenderLink, which auto-links ExcelUploadAccountingPackageNameId for the excel connector.
            SetCurrentSubmissionRequest(request);
            SetSelectedAccountingPackage(ExcelUploadAccountingPackageNameId);
            return;
        }

        SetCurrentSubmissionRequest(request);
    }

    React.useEffect(() => {
        if (isImportCustomerFinancialsActive) {
            ClearCurrentSubmissionRequest();
        }
    }, [isImportCustomerFinancialsActive, ClearCurrentSubmissionRequest])

    // The store-driven flow (confirm/connect/working/error modals) is rendered by DataCollectionImportFlow;
    // it renders nothing until a flow is in progress. While idle (flowView === 'none') we defer to
    // ImportCustomerFinancials, which is only active when GetImportCustomerFinancialsActive is true.
    return (
        <>
            <DataCollectionImportFlow />
            {flowView === 'none' && (
                <ImportCustomerFinancials
                    directOnly={importingDirect}
                    workspaceName={(props.customerFinancialsTarget && props.customerFinancialsTarget.workspaceName) || ''}
                    workspaceId={(props.customerFinancialsTarget && props.customerFinancialsTarget.workspaceId) || ''}
                    onSubmit={handleImportSubmit}
                    onCancel={() => { }}
                    externallyDisabled={false}
                    connectionOptions={connectionOptions}
                />
            )}
        </>
    )
}

export const FinancialsImportV2 = connect<InjectedReduxState, InjectedActionCreators, FinancialsImportProps, ApplicationState>(
    (appState: ApplicationState) => {
        const brandPalette = GetBrandConfig(appState)?.themeSettings?.palette;

        const result: InjectedReduxState = {
            isImportCustomerFinancialsActive: GetImportCustomerFinancialsActive(appState),
            customerFinancialsTarget: GetImportCustomerFinancialsTarget(appState),
            importingDirect: !!GetImportingDirect(appState),
            directLinkParameters: GetImportingDirect(appState),
            connectionOptions: GetConnectionOptions(appState),
            flowView: GetFinancialsImportFlowView(appState),
            brandAccentColor: brandPalette?.primaryColor || brandPalette?.controlBackground,
            accountingSystemText: GetPortalText<PortalTextAccountingSystems>(appState, 'accountingSystems'),
        };

        return result;
    },
    {
        ...NewDataCollectionParametersActions,
    }
)(FinancialsImportV2Component as any);
